import { useRef, useState } from "react";
import { UploadCloud, Loader2, AlertTriangle, Check, X } from "lucide-react";
import { T } from "../../ui/tokens";
import { fmt } from "../../lib/format";
import { apiFetch } from "../../lib/api";
import type { MovimentoExtrato } from "./types";

interface DadosExtraidos {
  tipo_documento?: string;
  numero_documento?: string | null;
  nif_emitente?: string | null;
  nome_emitente?: string | null;
  nif_recetor?: string | null;
  data_documento?: string | null;
  valor_total?: number | null;
  eh_nota_credito?: boolean;
}

interface Props {
  movimento: MovimentoExtrato;
  empresaNif: string;
  desativado: boolean;
  // Chamado com o id da fatura acabada de criar+arquivar. Quem concilia é o
  // painel (mesmo caminho de sempre: POST /api/reconciliacao/matches/manual),
  // nunca este componente -- para não haver duas formas de criar um match.
  onFaturaCriada: (faturaId: number) => void;
}

type Fase = "vazio" | "a_extrair" | "rever" | "a_criar";

// Arrastar um PDF que ainda NÃO está no sistema para cima de um movimento
// bancário: extrai os dados pela mesma IA do pipeline de email, mostra-os
// para confirmação/correção, e só depois cria a fatura, arquiva o PDF no
// Drive e concilia.
//
// O passo de confirmação é DELIBERADO e não deve ser removido: a convenção
// do projeto é nunca mexer em estado de dinheiro sem clique humano explícito
// (ver CLAUDE.md), e este fluxo acaba com a fatura marcada como paga. Além
// disso a IA erra valores de vez em quando -- criar às cegas produziria uma
// fatura errada JÁ dada como paga, o pior dos dois mundos.
export default function DropFaturaNova({ movimento: m, empresaNif, desativado, onFaturaCriada }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fase, setFase] = useState<Fase>("vazio");
  const [dragOver, setDragOver] = useState(false);
  const [ficheiro, setFicheiro] = useState<File | null>(null);
  const [dados, setDados] = useState<DadosExtraidos | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [avisoEmpresa, setAvisoEmpresa] = useState<string | null>(null);

  const reset = () => {
    setFase("vazio"); setFicheiro(null); setDados(null); setErro(null); setAvisoEmpresa(null);
  };

  const extrair = async (file: File) => {
    setFicheiro(file);
    setFase("a_extrair");
    setErro(null);
    setAvisoEmpresa(null);
    try {
      const fd = new FormData();
      fd.append("ficheiro", file);
      const r = await apiFetch("/api/extracao/analisar-documento", { method: "POST", body: fd });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErro(typeof d?.detail === "string" ? d.detail : "Não foi possível ler este documento.");
        setFase("vazio");
        return;
      }
      const resposta = await r.json();
      // PDF com VÁRIAS faturas dentro (ex: Via Verde mensal) não cabe neste
      // fluxo 1:1 -- em vez de escolher uma à sorte, encaminha para o ecrã
      // que trata disso a sério.
      if (resposta.documento_composto) {
        setErro("Este PDF tem vários documentos lá dentro. Usa o Processamento Assistido para o tratar e volta aqui depois.");
        setFase("vazio");
        return;
      }
      const d: DadosExtraidos = resposta.dados_extraidos || {};
      if (d.nif_recetor && d.nif_recetor !== empresaNif) {
        setAvisoEmpresa(
          `O documento parece ser da empresa ${d.nif_recetor}, mas este movimento é da ${empresaNif}. ` +
          "A fatura vai ser criada na empresa do movimento — confirma que é mesmo o documento certo."
        );
      }
      setDados({
        ...d,
        // O recetor é sempre a empresa do MOVIMENTO: é a única que pode ser
        // conciliada com ele (criar_match_manual rejeita empresas diferentes).
        nif_recetor: empresaNif,
        valor_total: d.valor_total != null ? Math.abs(d.valor_total) : Math.abs(m.valor),
      });
      setFase("rever");
    } catch (e) {
      setErro((e as Error)?.message || "Erro de rede.");
      setFase("vazio");
    }
  };

  const criarEConciliar = async () => {
    if (!dados || !ficheiro) return;
    setFase("a_criar");
    setErro(null);
    try {
      // 1. Criar a fatura -- mesmo endpoint do Processamento Assistido, por
      //    isso herda a deteção de duplicados de graça (registar_fatura).
      const rCriar = await apiFetch("/api/faturas", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          numero_documento: dados.numero_documento || "S/N",
          nome_emitente: dados.nome_emitente || "Desconhecido",
          nif_emitente: dados.nif_emitente || "999999990",
          nif_recetor: empresaNif,
          valor_total: Math.abs(Number(dados.valor_total) || 0),
          data_documento: dados.data_documento || m.data_mov,
          tipo_documento: m.tipo === "credito" ? "Fatura_Cliente" : "Fatura_Fornecedor",
          eh_nota_credito: !!dados.eh_nota_credito,
        }),
      });
      if (!rCriar.ok) {
        const d = await rCriar.json().catch(() => ({}));
        setErro(typeof d?.detail === "string" ? d.detail : "Não foi possível criar a fatura.");
        setFase("rever");
        return;
      }
      // POST /api/faturas devolve dataclasses.asdict(FaturaProcessada) --
      // o id vem no topo. Se por alguma razão não vier, PARA aqui em vez de
      // seguir: a fatura já existe, e tentar recriá-la produziria duplicados.
      const criada = await rCriar.json();
      const faturaId = criada?.id;
      if (!faturaId) {
        setErro("A fatura foi criada mas a resposta não trouxe o id — confirma no Arquivo (não repitas, senão duplicas).");
        setFase("rever");
        return;
      }

      // 2. Arquivar o PDF no Drive. Falha aqui NÃO aborta a conciliação: a
      //    fatura já existe e o pagamento é o que interessa reconciliar --
      //    fica só sem PDF arquivado, corrigível depois no Arquivo.
      try {
        const fd = new FormData();
        fd.append("ficheiro", ficheiro);
        await apiFetch(`/api/faturas/${faturaId}/arquivar`, { method: "POST", body: fd });
      } catch {
        /* sem impacto na conciliação */
      }

      // 3. Conciliar -- feito pelo painel, caminho normal de sempre.
      onFaturaCriada(faturaId);
      reset();
    } catch (e) {
      setErro((e as Error)?.message || "Erro de rede.");
      setFase("rever");
    }
  };

  const campo = (rotulo: string, valor: string, ao: (v: string) => void, tipo = "text") => (
    <label style={{ display: "flex", flexDirection: "column", gap: 3, flex: "1 1 46%", minWidth: 0 }}>
      <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: T.textLight }}>{rotulo}</span>
      <input
        value={valor}
        type={tipo}
        onChange={(e) => ao(e.target.value)}
        style={{ padding: "6px 9px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12.5, color: T.text, background: T.surface, outline: "none", minWidth: 0 }}
      />
    </label>
  );

  if (fase === "rever" && dados) {
    const valorNum = Math.abs(Number(dados.valor_total) || 0);
    const divergeValor = Math.abs(valorNum - Math.abs(m.valor)) > 0.02;
    return (
      <div style={{ border: `1px solid ${T.blueBorder}`, background: T.blueBg, borderRadius: 9, padding: "11px 13px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: T.blue }}>Confere os dados lidos do PDF</span>
          <button onClick={reset} title="Cancelar" style={{ border: "none", background: "none", cursor: "pointer", color: T.textMid, display: "inline-flex" }}>
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {campo("Fornecedor", dados.nome_emitente || "", (v) => setDados({ ...dados, nome_emitente: v }))}
          {campo("NIF", dados.nif_emitente || "", (v) => setDados({ ...dados, nif_emitente: v }))}
          {campo("Nº documento", dados.numero_documento || "", (v) => setDados({ ...dados, numero_documento: v }))}
          {campo("Data", dados.data_documento || "", (v) => setDados({ ...dados, data_documento: v }), "date")}
          {campo("Valor (€)", String(dados.valor_total ?? ""), (v) => setDados({ ...dados, valor_total: parseFloat(v.replace(",", ".")) }), "text")}
        </div>

        {divergeValor && (
          <div style={{ marginTop: 8, fontSize: 11.5, color: T.red, display: "flex", alignItems: "flex-start", gap: 5 }}>
            <AlertTriangle size={12} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} />
            O valor lido ({fmt(valorNum)}) não bate certo com o movimento ({fmt(Math.abs(m.valor))}). Corrige antes de continuar, ou confirma se é mesmo assim.
          </div>
        )}
        {avisoEmpresa && (
          <div style={{ marginTop: 8, fontSize: 11.5, color: T.amber, display: "flex", alignItems: "flex-start", gap: 5 }}>
            <AlertTriangle size={12} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} />
            {avisoEmpresa}
          </div>
        )}
        {erro && <div style={{ marginTop: 8, fontSize: 11.5, color: T.red }}>✗ {erro}</div>}

        <button
          disabled={desativado}
          onClick={criarEConciliar}
          style={{ marginTop: 10, width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "none", background: T.green, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: desativado ? "default" : "pointer", opacity: desativado ? 0.5 : 1 }}
        >
          <Check size={14} strokeWidth={2.5} /> Criar fatura, arquivar e dar como paga
        </button>
      </div>
    );
  }

  const aTrabalhar = fase === "a_extrair" || fase === "a_criar";

  return (
    <div>
      <style>{"@keyframes spin-drop { to { transform: rotate(360deg); } }"}</style>
      <div
        onClick={() => !aTrabalhar && !desativado && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          const f = e.dataTransfer.files[0];
          if (f && !aTrabalhar && !desativado) extrair(f);
        }}
        style={{
          border: `2px dashed ${dragOver ? T.blue : T.border}`, borderRadius: 9,
          padding: "14px 12px", textAlign: "center", background: dragOver ? T.blueBg : T.bg,
          cursor: aTrabalhar || desativado ? "default" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}
      >
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) extrair(f); }} />
        {aTrabalhar
          ? <Loader2 size={15} color={T.blue} style={{ animation: "spin-drop .8s linear infinite" }} />
          : <UploadCloud size={15} color={T.textMid} />}
        <span style={{ fontSize: 12, fontWeight: 600, color: T.textMid }}>
          {fase === "a_extrair" ? "A ler o documento…"
            : fase === "a_criar" ? "A criar e arquivar…"
            : "A fatura não está no sistema? Arrasta o PDF para aqui"}
        </span>
      </div>
      {erro && <div style={{ marginTop: 7, fontSize: 11.5, color: T.red }}>✗ {erro}</div>}
    </div>
  );
}
